import type { AcceloTaskGroup, PublishPayload, PublishRowInput } from "@/services/timesheetService";
import {
  TIMESHEET_DAMAGED_KEY,
  TIMESHEET_DELETED_KEY,
  TIMESHEET_STORAGE_KEY,
} from "@/state/timesheet";
import type {
  StoredTimesheet,
  StoredTimesheetDeletion,
  StoredTimesheetRow,
} from "@/state/timesheet";

/**
 * One timed row on the Timesheet page. It is stored as it is held.
 * `publishedMs` and `activityIds` record what has landed in Accelo. `published`
 * is the old lock. A stored `true` is read as `publishedMs`, and nothing sets
 * or writes it any more.
 *
 * The two landed fields and `beganAt` are optional here, and only here, so a
 * row literal written before they existed still compiles. `createRow` and
 * `readRows` always set them, and `writeRows` always stores them. Absent reads
 * as nothing landed, and as no recorded start.
 */
export type TimesheetRow = Omit<StoredTimesheetRow, "publishedMs" | "activityIds" | "beganAt"> &
  Partial<Pick<StoredTimesheetRow, "publishedMs" | "activityIds" | "beganAt">> & {
    published?: true;
  };

const NO_ACTIVITY_IDS: readonly string[] = [];

/** The milliseconds of this row that Accelo already holds. */
export function landedMs(row: TimesheetRow): number {
  return row.publishedMs ?? 0;
}

/** Every Accelo activity this row has produced, oldest first. */
export function activityIdsOf(row: TimesheetRow): readonly string[] {
  return row.activityIds ?? NO_ACTIVITY_IDS;
}

/** What the last publish did to one row, shown under its task. */
export interface RowOutcome {
  kind: "published" | "already-published" | "landed-unconfirmed" | "skipped" | "failed" | "unnamed";
  /** The route's sentence where it gave one. */
  message: string;
  /** The Accelo activity the answer named, where it named one. */
  activityId?: string;
}

/** One row as a publish sends it: the route's input plus the whole seconds to log. */
export type PublishRowSend = PublishRowInput & { seconds: number };

export const PUBLISHED_LABEL = "Published to Accelo";
export const LANDED_NO_ID_MESSAGE =
  "Accelo answered published but named no activity. Check Accelo before you publish this row again.";

/** The time is in Accelo under this activity, and a check about it failed. */
export function landedUnconfirmedMessage(activityId: string): string {
  return `This time is in Accelo as activity ${activityId}. The stored amount is not confirmed. Check it in Accelo.`;
}
export const SKIPPED_FALLBACK = "This row was not sent.";
export const FAILED_FALLBACK = "This row was not published. Try again.";
export const UNNAMED_MESSAGE =
  "Accelo gave no answer for this row. It is not marked published. Try again.";
export const PUBLISH_FAILED_MESSAGE =
  "Your time could not be published. No row is marked published. Try again in a few minutes.";
export const NO_SESSION_MESSAGE =
  "Your session could not be verified. Refresh the page and try again.";

/** The Accelo task list as the page holds it: on its way, failed, or here. */
export type TaskListState =
  | { kind: "loading" }
  | { kind: "error" }
  /** The route found no single Accelo staff member. `message` is the route's sentence. */
  | { kind: "refused"; message: string }
  | { kind: "ready"; groups: readonly AcceloTaskGroup[] };

export const TASKS_LOADING_LABEL = "Loading tasks…";
export const TASKS_ERROR_LABEL = "Could not load your Accelo tasks";
export const TASKS_REFUSED_LABEL = "No Accelo tasks available";

/** The one option of a disabled dropdown. */
export function tasksAwayLabel(kind: "loading" | "error" | "refused"): string {
  if (kind === "loading") return TASKS_LOADING_LABEL;
  return kind === "error" ? TASKS_ERROR_LABEL : TASKS_REFUSED_LABEL;
}

/** One segment of the Tracked today bar. */
export interface TaskSegment {
  /** Accelo task id, or `NO_TASK_KEY` for the time on rows with no task. */
  key: string;
  /** Direct label: the task title, or `NO_TASK_LABEL`. */
  label: string;
  ms: number;
  /** A CSS colour token reference, never a hex. */
  colour: string;
}

/** How many rows can publish, over the total. */
export interface ReadyCount {
  ready: number;
  total: number;
  /** Rows still short of a name or a task. */
  short: number;
}

export const NO_TASK_KEY = "none";
export const NO_TASK_LABEL = "No task yet";

/** The chart accents, in token order. They repeat past the fourth segment. */
export const SEGMENT_COLOURS: readonly string[] = [
  "var(--brand)",
  "var(--chart-teal)",
  "var(--chart-amber)",
  "var(--chart-purple)",
];

function pad(value: number): string {
  return value < 10 ? `0${value}` : `${value}`;
}

/** Milliseconds as `HH:MM:SS`. Hours grow past two digits rather than wrap. */
export function formatElapsed(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  return `${pad(Math.floor(seconds / 3600))}:${pad(Math.floor((seconds % 3600) / 60))}:${pad(seconds % 60)}`;
}

/** The most time one row may be set to by hand: one whole day. */
export const MAX_ELAPSED_MS = 86_400_000;

/**
 * `HH:MM:SS`, `H:MM:SS`, `HH:MM` or `H:MM` as milliseconds. Anything else is
 * `null`. Minutes and seconds stop at 59. A value above 24 hours is `null`.
 */
export function parseElapsed(text: string): number | null {
  const parts = /^(\d+):([0-5]\d)(?::([0-5]\d))?$/.exec(text.trim());
  if (parts === null) return null;
  const hours = Number(parts[1]);
  const minutes = Number(parts[2]);
  const seconds = parts[3] === undefined ? 0 : Number(parts[3]);
  const ms = hours * 3_600_000 + minutes * 60_000 + seconds * 1_000;
  return ms > MAX_ELAPSED_MS ? null : ms;
}

const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
/** The earliest millisecond a row id may decode to. Anything older is noise. */
const EARLIEST_ROW_MS = Date.UTC(2020, 0, 1);
const WEEKDAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** The local day of an epoch millisecond, as `YYYY-MM-DD`. */
export function dayKey(ms: number): string {
  const at = new Date(ms);
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
}

/** The browser's IANA timezone, `undefined` when the platform names none. */
function localZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

interface WallClock {
  year: number;
  month: number;
  date: number;
  hours: number;
  minutes: number;
  seconds: number;
}

/**
 * The wall clock of an epoch millisecond in `zone`. The browser's own zone
 * reads it when `zone` is absent or is not a timezone this browser knows.
 */
function wallClockIn(ms: number, zone: string | undefined): WallClock {
  if (zone !== undefined) {
    try {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: zone,
        hourCycle: "h23",
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
      }).formatToParts(ms);
      const part = (type: Intl.DateTimeFormatPartTypes) =>
        Number(parts.find((p) => p.type === type)?.value);
      return {
        year: part("year"),
        month: part("month"),
        date: part("day"),
        hours: part("hour"),
        minutes: part("minute"),
        seconds: part("second"),
      };
    } catch {
      // An unknown zone reads in the browser's own zone below.
    }
  }
  const at = new Date(ms);
  return {
    year: at.getFullYear(),
    month: at.getMonth() + 1,
    date: at.getDate(),
    hours: at.getHours(),
    minutes: at.getMinutes(),
    seconds: at.getSeconds(),
  };
}

/** The day of an epoch millisecond in `zone`, as `YYYY-MM-DD`. */
function dayKeyIn(ms: number, zone: string | undefined): string {
  const at = wallClockIn(ms, zone);
  return `${at.year}-${pad(at.month)}-${pad(at.date)}`;
}

/** The epoch millisecond midnight begins the given calendar day in `zone`. */
function midnightIn(year: number, month: number, date: number, zone: string): number {
  const offset = (ms: number) => {
    const at = wallClockIn(ms, zone);
    const wall = Date.UTC(at.year, at.month - 1, at.date, at.hours, at.minutes, at.seconds);
    return wall - Math.floor(ms / 1000) * 1000;
  };
  const wall = Date.UTC(year, month - 1, date);
  return wall - offset(wall - offset(wall));
}

/** A day string the page can trust: `YYYY-MM-DD` and a real calendar day. */
function isDayKey(value: unknown): value is string {
  return typeof value === "string" && DAY_PATTERN.test(value);
}

function dayParts(day: string): { year: number; month: number; date: number } | null {
  if (!isDayKey(day)) return null;
  const parts = day.split("-");
  return { year: Number(parts[0]), month: Number(parts[1]), date: Number(parts[2]) };
}

/**
 * The millisecond a row id encodes. `newRowId` writes `Date.now().toString(36)`,
 * so base 36 reads the millisecond back. `null` when the id does not decode to
 * a millisecond between 2020 and one day after now.
 */
export function msFromRowId(id: string): number | null {
  const ms = parseInt(id, 36);
  if (!Number.isFinite(ms)) return null;
  if (ms < EARLIEST_ROW_MS || ms > Date.now() + 86_400_000) return null;
  return ms;
}

/** The local day a row id encodes, `null` when it decodes to no usable one. */
export function dayFromRowId(id: string): string | null {
  const ms = msFromRowId(id);
  return ms === null ? null : dayKey(ms);
}

/** The day that many days later, or earlier. It crosses a month and a year. */
export function shiftDay(day: string, days: number): string {
  const parts = dayParts(day);
  if (parts === null) return day;
  return dayKey(new Date(parts.year, parts.month - 1, parts.date + days).getTime());
}

/** `Today`, `Yesterday`, `Tomorrow`, or a short date such as `Fri 18 Sep`. */
export function dayLabel(day: string, now: number): string {
  const today = dayKey(now);
  if (day === today) return "Today";
  if (day === shiftDay(today, -1)) return "Yesterday";
  if (day === shiftDay(today, 1)) return "Tomorrow";
  const parts = dayParts(day);
  if (parts === null) return day;
  const at = new Date(parts.year, parts.month - 1, parts.date);
  const weekday = WEEKDAY_NAMES[at.getDay()] ?? "";
  const month = MONTH_NAMES[at.getMonth()] ?? "";
  return `${weekday} ${at.getDate()} ${month}`;
}

/** Banked time plus the current run. Never negative, even if the clock steps back. */
export function elapsedMs(row: TimesheetRow, now: number): number {
  const run = row.running && row.startedAt !== null ? Math.max(0, now - row.startedAt) : 0;
  return row.accumulatedMs + run;
}

function newRowId(now: number): string {
  return `${now.toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * A fresh row: no name, no task, already counting from `now`. It belongs to
 * `day`, or to the local day of `now` when the caller names no day.
 */
export function createRow(now: number, id: string = newRowId(now), day?: string): TimesheetRow {
  return {
    id,
    name: "",
    taskId: "",
    date: day ?? dayKey(now),
    accumulatedMs: 0,
    startedAt: now,
    beganAt: now,
    running: true,
    publishedMs: 0,
    activityIds: [],
  };
}

/** Add a row at the top: the table reads newest first. */
export function addRow(rows: readonly TimesheetRow[], now: number, day?: string): TimesheetRow[] {
  return [createRow(now, undefined, day), ...rows];
}

/** Pause banks the run and clears `startedAt`. Resume starts a run at `now`. */
export function toggleRow(row: TimesheetRow, now: number): TimesheetRow {
  return row.running
    ? { ...row, running: false, accumulatedMs: elapsedMs(row, now), startedAt: null }
    : { ...row, running: true, startedAt: now };
}

/** Apply a change to the row with this id and leave the others as they are. */
export function updateRow(
  rows: readonly TimesheetRow[],
  id: string,
  change: (row: TimesheetRow) => TimesheetRow
): TimesheetRow[] {
  return rows.map((row) => (row.id === id ? change(row) : row));
}

/** The rows of one day, in the order they were given. */
export function rowsForDay(rows: readonly TimesheetRow[], day: string): TimesheetRow[] {
  return rows.filter((row) => row.date === day);
}

/** Every day that holds a row, newest first. */
export function daysWithRows(rows: readonly TimesheetRow[]): string[] {
  return [...new Set(rows.map((row) => row.date))].sort((a, b) => b.localeCompare(a));
}

/** The rows without the one with this id. */
export function removeRow(rows: readonly TimesheetRow[], id: string): TimesheetRow[] {
  return rows.filter((row) => row.id !== id);
}

/**
 * Move the row to another day. It keeps its name, task, time and running
 * state. The move is refused when a start of the row does not exist on the new
 * day, as 02:30 does not when daylight saving begins: carrying it would
 * silently rewrite the time Accelo is sent.
 */
export function setRowDate(rows: readonly TimesheetRow[], id: string, day: string): TimesheetRow[] {
  return updateRow(rows, id, (row) => {
    let moved: TimesheetRow = { ...row, date: day };
    // Both starts travel with the row, keeping their time of day. Moving a day
    // is not a pick, so a row nobody picked for stays unpicked.
    const began = onDay(autoStartMs(row), day);
    const picked = row.startOverrideMs === undefined ? undefined : onDay(row.startOverrideMs, day);
    if (middaySeconds(day) !== undefined && (began === null || picked === null)) return row;
    if (began !== null) moved.beganAt = began;
    if (picked === null) moved = clearStart(moved);
    else if (picked !== undefined) moved = withPick(moved, picked);
    return moved;
  });
}

/**
 * Move one row's start to `hhmm`, a 24-hour `HH:MM` on the row's own day. A
 * time the row cannot take changes nothing.
 */
export function setRowStart(
  rows: readonly TimesheetRow[],
  id: string,
  hhmm: string
): TimesheetRow[] {
  return updateRow(rows, id, (row) => (hhmm === "" ? clearStart(row) : withStart(row, hhmm)));
}

/**
 * Set the row's elapsed time by hand. A running row restarts its run at `now`,
 * so the page shows exactly `ms` and counts on from there. A negative `ms` is 0.
 */
export function setRowElapsed(
  rows: readonly TimesheetRow[],
  id: string,
  ms: number,
  now: number
): TimesheetRow[] {
  const banked = Math.max(0, ms);
  return updateRow(rows, id, (row) =>
    row.running
      ? { ...row, accumulatedMs: banked, startedAt: now }
      : { ...row, accumulatedMs: banked }
  );
}

/** Tracked today: the sum of every row's elapsed time. */
export function dayTotalMs(rows: readonly TimesheetRow[], now: number): number {
  return rows.reduce((sum, row) => sum + elapsedMs(row, now), 0);
}

/**
 * Split the day by Accelo task, in the order of the task list. Time on rows
 * with no task is one last segment. Every segment carries its own label.
 */
export function taskSplit(
  rows: readonly TimesheetRow[],
  now: number,
  groups: readonly AcceloTaskGroup[]
): TaskSegment[] {
  const totals = new Map<string, number>();
  let unset = 0;
  for (const row of rows) {
    const ms = elapsedMs(row, now);
    if (row.taskId === "") {
      unset += ms;
    } else {
      totals.set(row.taskId, (totals.get(row.taskId) ?? 0) + ms);
    }
  }

  const parts: { key: string; label: string; ms: number }[] = [];
  for (const group of groups) {
    for (const task of group.tasks) {
      const ms = totals.get(task.id);
      if (ms !== undefined) {
        parts.push({ key: task.id, label: task.title, ms });
        totals.delete(task.id);
      }
    }
  }
  // A stored task id the snapshot no longer lists still counts, under its id.
  for (const [taskId, ms] of totals) {
    parts.push({ key: taskId, label: taskId, ms });
  }
  if (rows.some((row) => row.taskId === "")) {
    parts.push({ key: NO_TASK_KEY, label: NO_TASK_LABEL, ms: unset });
  }

  return parts.map((part, index) => ({
    ...part,
    colour: SEGMENT_COLOURS[index % SEGMENT_COLOURS.length] ?? "var(--brand)",
  }));
}

/** A row is ready when it carries both a name and a task. */
export function isReady(row: TimesheetRow): boolean {
  return row.name.trim() !== "" && row.taskId !== "";
}

/**
 * The whole seconds the next publish sends for this row: the time Accelo does
 * not hold yet. Never below 0.
 */
export function publishSeconds(row: TimesheetRow, now: number): number {
  return Math.max(0, Math.floor((elapsedMs(row, now) - landedMs(row)) / 1000));
}

/** Whether the row holds time Accelo does not. */
export function hasUnsentTime(row: TimesheetRow, now: number): boolean {
  return publishSeconds(row, now) > 0;
}

/**
 * Whether the row is finished with: Accelo holds some of its time, its clock
 * is stopped, and it holds nothing left to send. The page shows such a row in
 * the landed box. Publishing alone does not put a row here, because a publish
 * leaves the clock as it found it, so a resumed row leaves the landed box on
 * its own.
 */
export function isLanded(row: TimesheetRow, now: number): boolean {
  return landedMs(row) > 0 && !row.running && !hasUnsentTime(row, now);
}

/**
 * The rows the page selects before the person touches anything: not running,
 * ready, and holding unsent time. A running row is never here.
 */
export function defaultSelection(rows: readonly TimesheetRow[], now: number): string[] {
  return rows
    .filter((row) => !row.running && isReady(row) && hasUnsentTime(row, now))
    .map((row) => row.id);
}

/**
 * Only a row with unsent time counts: a day whose rows have all landed reads
 * `0 of 0`. Without `now`, a running row counts as holding unsent time.
 */
export function readyCount(rows: readonly TimesheetRow[], now?: number): ReadyCount {
  const open = rows.filter((row) =>
    now === undefined
      ? row.running || row.accumulatedMs - landedMs(row) >= 1000
      : hasUnsentTime(row, now)
  );
  const ready = open.filter(isReady).length;
  return { ready, total: open.length, short: open.length - ready };
}

/**
 * The epoch second of 12:00 local time on `day`. Midday keeps the day right at
 * every timezone offset. `undefined` when `day` is not a real calendar day.
 */
export function middaySeconds(day: string): number | undefined {
  const parts = dayParts(day);
  if (parts === null) return undefined;
  const at = new Date(parts.year, parts.month - 1, parts.date, 12, 0, 0, 0);
  const ms = at.getTime();
  // A rolled-over date, such as 2026-02-31, is not the day it was written as.
  if (!Number.isFinite(ms) || dayKey(ms) !== day) return undefined;
  return Math.floor(ms / 1000);
}

/**
 * The epoch millisecond a row's work began, in order of use: the start a person
 * recorded, then the creation time its id encodes when that falls on the row's
 * own day, then 12:00 local on that day, then the read. It is never dropped for
 * a bad start, in the way `storedDay` is never dropped for a bad day.
 */
export function autoStartMs(row: { id: string; date: string; beganAt?: unknown }): number {
  // A start that fell on another day is not this row's start. The DAY cell
  // moves both together; anything else that moved the day is not trusted here.
  if (
    typeof row.beganAt === "number" &&
    Number.isFinite(row.beganAt) &&
    dayKey(row.beganAt) === row.date
  ) {
    return row.beganAt;
  }
  const minted = msFromRowId(row.id);
  if (minted !== null && dayKey(minted) === row.date) return minted;
  const midday = middaySeconds(row.date);
  return midday === undefined ? (minted ?? Date.now()) : midday * 1000;
}

/**
 * The epoch millisecond a row's work began: the start a person picked from the
 * START list, or the row's own start when nobody has picked one.
 */
export function startMs(row: {
  id: string;
  date: string;
  beganAt?: unknown;
  startOverrideMs?: unknown;
  startOverrideZone?: unknown;
}): number {
  return pickedStartMs(row) ?? autoStartMs(row);
}

/**
 * The start a person picked, while it falls on the row's own day in the
 * timezone it was picked in. Opening the page in another timezone keeps it.
 */
function pickedStartMs(row: {
  date: string;
  startOverrideMs?: unknown;
  startOverrideZone?: unknown;
}): number | undefined {
  const picked = row.startOverrideMs;
  if (typeof picked !== "number" || !Number.isFinite(picked)) return undefined;
  const zone = typeof row.startOverrideZone === "string" ? row.startOverrideZone : undefined;
  return dayKeyIn(picked, zone) === row.date ? picked : undefined;
}

/** Whether a person picked this row's start, rather than the row setting it. */
export function isStartPicked(
  row: Pick<TimesheetRow, "date" | "startOverrideMs" | "startOverrideZone">
): boolean {
  return pickedStartMs(row) !== undefined;
}

/**
 * The epoch second a send tells Accelo the work started. A row publishes only
 * the time Accelo does not hold yet, so a later send starts where the last one
 * left off: the row's start plus the time already landed. That second is held
 * to the last second of the row's own day, because Accelo logs the entry on
 * the day `date_started` falls on: a row begun at 23:30 with an hour landed
 * would otherwise send its next hour on the following day. `undefined` when
 * the row's day is not a real calendar day, which the route refuses, as it did
 * when this second was the day's midday.
 */
export function sendStartSeconds(row: TimesheetRow): number | undefined {
  const parts = dayParts(row.date);
  if (parts === null || middaySeconds(row.date) === undefined) return undefined;
  // A picked start ends its day in the timezone it was picked in.
  const zone = pickedStartMs(row) === undefined ? undefined : row.startOverrideZone;
  const nextDayMs =
    zone === undefined
      ? new Date(parts.year, parts.month - 1, parts.date + 1).getTime()
      : midnightIn(parts.year, parts.month, parts.date + 1, zone);
  const nextDaySeconds = Math.floor(nextDayMs / 1000);
  return Math.min(Math.floor((startMs(row) + landedMs(row)) / 1000), nextDaySeconds - 1);
}

/**
 * The row with a person's picked start set to `hhmm`, a 24-hour `HH:MM`, on its
 * own day. It writes `startOverrideMs` and never touches `beganAt`, so the
 * row's own start survives to come back when the pick is cleared. The row is
 * returned unchanged when the text is not a time of day, when that time does
 * not exist on the row's day because the clock skips it, or when its day is not
 * a real calendar day. Only the time of day moves. The `DAY` cell stays the one
 * way to move a row to another day.
 */
export function withStart(row: TimesheetRow, hhmm: string): TimesheetRow {
  const match = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (match === null) return row;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return row;
  const parts = dayParts(row.date);
  if (parts === null) return row;
  const at = new Date(parts.year, parts.month - 1, parts.date, hours, minutes, 0, 0);
  const ms = at.getTime();
  if (!Number.isFinite(ms) || dayKey(ms) !== row.date) return row;
  // A time the clock skips, such as 02:30 when daylight saving begins, comes
  // back an hour later. That is not the time the person picked, so it is refused.
  if (at.getHours() !== hours || at.getMinutes() !== minutes) return row;
  return withPick(row, ms);
}

/** The row with a pick at `ms`, made in the browser's own timezone. */
function withPick(row: TimesheetRow, ms: number): TimesheetRow {
  const next: TimesheetRow = { ...row, startOverrideMs: ms };
  const zone = localZone();
  if (zone === undefined) delete next.startOverrideZone;
  else next.startOverrideZone = zone;
  return next;
}

/**
 * Whether the 24-hour `HH:MM` exists on `day` in local time. `false` for a time
 * the clock skips, such as 02:30 when daylight saving begins, and for a day
 * that is not a real calendar day. The START list disables what this refuses.
 */
export function startExistsOn(day: string, hhmm: string): boolean {
  return withStart({ id: "", date: day } as TimesheetRow, hhmm).startOverrideMs !== undefined;
}

/** Drop a person's pick, so the row reads its own start again. */
export function clearStart(row: TimesheetRow): TimesheetRow {
  if (row.startOverrideMs === undefined && row.startOverrideZone === undefined) return row;
  const next: TimesheetRow = { ...row };
  delete next.startOverrideMs;
  delete next.startOverrideZone;
  return next;
}

/**
 * The same time of day as `ms`, on `day`. `null` when `day` is not a real day,
 * or when that time does not exist on it because the clock skips it.
 */
function onDay(ms: number, day: string): number | null {
  const parts = dayParts(day);
  if (parts === null) return null;
  const at = new Date(ms);
  const moved = new Date(
    parts.year,
    parts.month - 1,
    parts.date,
    at.getHours(),
    at.getMinutes(),
    at.getSeconds(),
    at.getMilliseconds()
  );
  const out = moved.getTime();
  if (moved.getHours() !== at.getHours() || moved.getMinutes() !== at.getMinutes()) return null;
  return Number.isFinite(out) && dayKey(out) === day ? out : null;
}

/** Every half hour of the day as canonical `HH:MM`, 48 in all. The START list. */
export function halfHourPresets(): string[] {
  const out: string[] = [];
  for (let minutes = 0; minutes < 1440; minutes += 30) {
    out.push(`${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`);
  }
  return out;
}

function clockLabel(hours: number, minutes: number): string {
  const suffix = hours < 12 ? "AM" : "PM";
  const shown = hours % 12 === 0 ? 12 : hours % 12;
  return `${shown}:${pad(minutes)} ${suffix}`;
}

/** An epoch millisecond on the 12-hour clock the row shows, as `10:40 PM`. */
export function formatClockLabel(ms: number | undefined): string {
  if (ms === undefined) return "—";
  const at = new Date(ms);
  return clockLabel(at.getHours(), at.getMinutes());
}

/** A canonical `HH:MM` on the 12-hour clock, as `10:40 PM`. Unreadable text passes through. */
export function clockLabelOf(hhmm: string): string {
  const match = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (match === null) return hhmm;
  return clockLabel(Number(match[1]), Number(match[2]));
}

/** A start or end millisecond as `09:05`, the 24-hour clock the row shows. */
export function formatTimeOfDay(ms: number | undefined): string {
  if (ms === undefined) return "—";
  const at = new Date(ms);
  return `${String(at.getHours()).padStart(2, "0")}:${String(at.getMinutes()).padStart(2, "0")}`;
}

/**
 * The rows a publish sends: the rows whose id is in `selected`, or
 * `defaultSelection` when the caller names none. A row with no unsent time is
 * never sent. Each row carries `seconds`, the time Accelo does not hold yet,
 * and the second its work started, so it lands in Accelo at that time of day.
 * `elapsedMs` still travels for the current route. The route skips a row short
 * of a name, a task or a valid day and says why.
 */
export function publishInputs(
  rows: readonly TimesheetRow[],
  now: number,
  selected: readonly string[] = defaultSelection(rows, now)
): PublishRowSend[] {
  const chosen = new Set(selected);
  return rows
    .filter((row) => chosen.has(row.id) && hasUnsentTime(row, now))
    .map((row) => {
      const dateSeconds = sendStartSeconds(row);
      return {
        rowId: row.id,
        name: row.name,
        taskId: row.taskId,
        elapsedMs: elapsedMs(row, now),
        seconds: publishSeconds(row, now),
        ...(dateSeconds === undefined ? {} : { dateSeconds }),
      };
    });
}

/** The outcome of a row Accelo holds while the answer does not prove the amount. */
function landedUnconfirmed(activityId: string | null): RowOutcome {
  return activityId === null
    ? { kind: "landed-unconfirmed", message: LANDED_NO_ID_MESSAGE }
    : { kind: "landed-unconfirmed", message: landedUnconfirmedMessage(activityId), activityId };
}

function outcomeOf(entry: unknown): { rowId: string; outcome: RowOutcome } | null {
  if (typeof entry !== "object" || entry === null) return null;
  const { rowId, outcome, message, activityId } = entry as Record<string, unknown>;
  if (typeof rowId !== "string") return null;
  const said = typeof message === "string" && message.trim() !== "" ? message : null;
  const activity = typeof activityId === "string" && activityId.trim() !== "" ? activityId : null;
  switch (outcome) {
    case "published":
      // A published answer with no activity id is not proof that the time landed.
      return activity === null
        ? { rowId, outcome: landedUnconfirmed(null) }
        : {
            rowId,
            outcome: { kind: "published", message: PUBLISHED_LABEL, activityId: activity },
          };
    case "already-published":
      return {
        rowId,
        outcome: {
          kind: "already-published",
          message: said ?? PUBLISHED_LABEL,
          ...(activity === null ? {} : { activityId: activity }),
        },
      };
    case "skipped":
      return { rowId, outcome: { kind: "skipped", message: said ?? SKIPPED_FALLBACK } };
    case "failed":
      // Accelo holds the entry and a check about it failed. Never ask for a retry.
      return activity === null
        ? { rowId, outcome: { kind: "failed", message: said ?? FAILED_FALLBACK } }
        : { rowId, outcome: landedUnconfirmed(activity) };
    default:
      return null;
  }
}

const LANDED_KINDS: readonly string[] = ["published", "already-published", "landed-unconfirmed"];

/** The whole seconds one sent row carried. An input without `seconds` sent its elapsed time. */
function sentSeconds(input: PublishRowInput): number {
  const { seconds } = input as Partial<PublishRowSend>;
  return typeof seconds === "number" && Number.isFinite(seconds)
    ? Math.max(0, seconds)
    : Math.max(0, Math.floor(input.elapsedMs / 1000));
}

/**
 * Apply the route's answer. A refusal, an unreadable answer, or a row the
 * answer does not name marks nothing. A `published` row adds the seconds that
 * were sent to `publishedMs` and gains the activity id. It is never frozen: its
 * run, its clock and its banked time stay as they are. `already-published`
 * changes nothing on the row. `landed-unconfirmed` gains the activity id and
 * adds nothing to `publishedMs`.
 */
export function applyPublishAnswer(
  rows: readonly TimesheetRow[],
  sent: readonly PublishRowInput[],
  payload: PublishPayload
): { rows: TimesheetRow[]; outcomes: Record<string, RowOutcome> } {
  const outcomes: Record<string, RowOutcome> = {};
  const answered = payload as { status?: unknown; rows?: unknown };
  if (
    (answered.status !== "ok" && answered.status !== "incomplete") ||
    !Array.isArray(answered.rows)
  ) {
    return { rows: [...rows], outcomes };
  }

  const secondsSent = new Map(sent.map((row) => [row.rowId, sentSeconds(row)]));
  for (const entry of answered.rows as unknown[]) {
    const named = outcomeOf(entry);
    if (named === null || !secondsSent.has(named.rowId)) continue;
    // If the answer names a row twice, a landed entry wins: the time is in Accelo.
    const landedBefore = LANDED_KINDS.includes(outcomes[named.rowId]?.kind ?? "");
    if (!landedBefore) outcomes[named.rowId] = named.outcome;
  }
  for (const rowId of secondsSent.keys()) {
    outcomes[rowId] ??= { kind: "unnamed", message: UNNAMED_MESSAGE };
  }

  const next = rows.map((row): TimesheetRow => {
    const outcome = outcomes[row.id];
    if (outcome === undefined) return row;
    if (outcome.kind !== "published" && outcome.kind !== "landed-unconfirmed") return row;
    const activityId = outcome.activityId;
    const held = activityIdsOf(row);
    const gained = activityId !== undefined && !held.includes(activityId);
    const activityIds = gained ? [...held, activityId] : [...held];
    if (outcome.kind === "landed-unconfirmed") {
      return gained ? { ...row, activityIds } : row;
    }
    return {
      ...row,
      publishedMs: landedMs(row) + (secondsSent.get(row.id) ?? 0) * 1000,
      activityIds,
    };
  });
  return { rows: next, outcomes };
}

/**
 * The day a stored row belongs to. A row written before the field existed, or
 * one with a broken day, takes the day its id decodes to, then the day of the
 * read. It is never dropped for a bad day.
 */
function storedDay(row: { id: string; date?: unknown }): string {
  if (isDayKey(row.date)) return row.date;
  return dayFromRowId(row.id) ?? dayKey(Date.now());
}

/** A stored row as it may read back: written before the landed fields existed, or with the old lock. */
type ReadBackRow = Omit<StoredTimesheetRow, "publishedMs" | "activityIds" | "beganAt"> & {
  publishedMs?: unknown;
  activityIds?: unknown;
  beganAt?: unknown;
  published?: unknown;
};

function isStoredRow(value: unknown): value is ReadBackRow {
  if (typeof value !== "object" || value === null) return false;
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "string" &&
    typeof row.name === "string" &&
    typeof row.taskId === "string" &&
    typeof row.accumulatedMs === "number" &&
    Number.isFinite(row.accumulatedMs) &&
    (row.startedAt === null ||
      (typeof row.startedAt === "number" && Number.isFinite(row.startedAt))) &&
    typeof row.running === "boolean"
  );
}

/**
 * A stored row as the page holds it. The old `published: true` lock reads as
 * all of the row's banked time landed. A row with neither landed field reads
 * as nothing landed. `published` itself is dropped.
 */
function migrateRow(stored: ReadBackRow): TimesheetRow {
  const {
    published,
    publishedMs,
    activityIds,
    beganAt,
    startOverrideMs,
    startOverrideZone,
    ...row
  } = stored;
  const landed =
    typeof publishedMs === "number" && Number.isFinite(publishedMs)
      ? Math.max(0, publishedMs)
      : published === true
        ? row.accumulatedMs
        : 0;
  const date = storedDay(row);
  // A pick is kept only while it still lands on the row's own day in the zone
  // it was picked in. A row stored before the field existed carries none.
  const picked =
    pickedStartMs({ date, startOverrideMs, startOverrideZone }) === undefined
      ? {}
      : typeof startOverrideZone === "string"
        ? { startOverrideMs, startOverrideZone }
        : { startOverrideMs };
  return {
    ...row,
    date,
    ...picked,
    beganAt: autoStartMs({ id: row.id, date, beganAt }),
    publishedMs: landed,
    activityIds: Array.isArray(activityIds)
      ? activityIds.filter((id): id is string => typeof id === "string")
      : [],
  };
}

/** The rows of a raw stored value, and whether any part of it did not read. */
function isStoredDeletion(value: unknown): value is StoredTimesheetDeletion {
  if (typeof value !== "object" || value === null) return false;
  const deletion = value as Record<string, unknown>;
  return (
    typeof deletion.id === "string" &&
    typeof deletion.at === "number" &&
    Number.isFinite(deletion.at)
  );
}

function parseStore(raw: string): {
  rows: TimesheetRow[];
  deleted: StoredTimesheetDeletion[];
  damaged: boolean;
  carriesDeleted: boolean;
} {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { rows: [], deleted: [], damaged: true, carriesDeleted: false };
  }
  const entries = (parsed as Partial<StoredTimesheet> | null)?.rows;
  if (!Array.isArray(entries)) {
    return { rows: [], deleted: [], damaged: true, carriesDeleted: false };
  }
  const stored = (entries as unknown[]).filter(isStoredRow);
  const deletedEntries = (parsed as Partial<StoredTimesheet>).deleted;
  const deleted = Array.isArray(deletedEntries)
    ? (deletedEntries as unknown[]).filter(isStoredDeletion)
    : [];
  return {
    rows: stored.map(migrateRow),
    deleted,
    carriesDeleted: Object.prototype.hasOwnProperty.call(parsed, "deleted"),
    damaged:
      stored.length !== entries.length ||
      (Array.isArray(deletedEntries) && deleted.length !== deletedEntries.length),
  };
}

function parseDeleted(raw: string | null): StoredTimesheetDeletion[] {
  if (raw === null) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isStoredDeletion) : [];
  } catch {
    return [];
  }
}

function mergeDeletions(
  first: readonly StoredTimesheetDeletion[],
  second: readonly StoredTimesheetDeletion[]
): StoredTimesheetDeletion[] {
  const byId = new Map<string, StoredTimesheetDeletion>();
  for (const entry of [...first, ...second]) {
    const held = byId.get(entry.id);
    if (held === undefined || entry.at > held.at) byId.set(entry.id, entry);
  }
  return [...byId.values()];
}

const DELETION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

function writeDeleted(deleted: readonly StoredTimesheetDeletion[]): void {
  const current = deleted.filter(({ at }) => Date.now() - at <= DELETION_TTL_MS);
  window.localStorage.setItem(TIMESHEET_DELETED_KEY, JSON.stringify(current));
}

/**
 * Read the store. `damaged` is true when the stored value exists and does not
 * parse, or parses to something that is not a row array. A blocked store reads
 * as no rows and is not damaged: there is no value to keep.
 */
export function readStore(): {
  rows: TimesheetRow[];
  deleted: StoredTimesheetDeletion[];
  damaged: boolean;
} {
  try {
    const raw = window.localStorage.getItem(TIMESHEET_STORAGE_KEY);
    const stored =
      raw === null
        ? { rows: [], deleted: [], damaged: false, carriesDeleted: false }
        : parseStore(raw);
    const deleted = mergeDeletions(
      parseDeleted(window.localStorage.getItem(TIMESHEET_DELETED_KEY)),
      stored.deleted
    );
    if (raw !== null && stored.carriesDeleted) {
      writeDeleted(deleted);
      window.localStorage.setItem(TIMESHEET_STORAGE_KEY, JSON.stringify({ rows: stored.rows }));
    }
    const removed = new Set(deleted.map(({ id }) => id));
    return {
      rows: stored.rows.filter((row) => !removed.has(row.id)),
      deleted,
      damaged: stored.damaged,
    };
  } catch {
    return { rows: [], deleted: [], damaged: false };
  }
}

/** Read the stored rows. A blocked or damaged store reads as no rows. */
export function readRows(): TimesheetRow[] {
  return readStore().rows;
}

/**
 * Write the rows. A blocked store loses the write, never the page. The raw
 * value of a damaged store is copied to the damaged key before the first write
 * replaces it, and that copy is never overwritten. `published` is never written.
 */
export function writeRows(
  rows: readonly TimesheetRow[],
  deleted?: readonly StoredTimesheetDeletion[]
): void {
  try {
    const raw = window.localStorage.getItem(TIMESHEET_STORAGE_KEY);
    if (
      raw !== null &&
      parseStore(raw).damaged &&
      window.localStorage.getItem(TIMESHEET_DAMAGED_KEY) === null
    ) {
      window.localStorage.setItem(TIMESHEET_DAMAGED_KEY, raw);
    }
  } catch {
    // The copy could not be kept. The write below still runs.
  }
  try {
    const stored: StoredTimesheet = {
      rows: rows.map((held): StoredTimesheetRow => {
        const row: TimesheetRow = { ...held };
        delete row.published;
        return {
          ...row,
          beganAt: autoStartMs(held),
          publishedMs: landedMs(held),
          activityIds: [...activityIdsOf(held)],
        };
      }),
    };
    window.localStorage.setItem(TIMESHEET_STORAGE_KEY, JSON.stringify(stored));
    if (deleted !== undefined) writeDeleted(deleted);
  } catch {
    // Storage is blocked or full. The page keeps working from memory.
  }
}

/** Store a deletion before the page removes its row. Storage failure never stops the page. */
export function recordDeletion(id: string): void {
  try {
    const deleted = parseDeleted(window.localStorage.getItem(TIMESHEET_DELETED_KEY));
    writeDeleted([...deleted.filter((entry) => entry.id !== id), { id, at: Date.now() }]);
  } catch {
    // The persistence helpers already contain storage faults. Keep this contract absolute.
  }
}
